/**
 * Top-of-page header with brand, current-user identity, and a Profile link.
 * Reused on the My Apps page and the app workspace.
 *
 * Shows an "Admin" link when /me reports isPlatformAdmin=true.
 *
 * NOTE: The Log Out button used to live here. It now lives on /profile —
 * clicking the user-info block on the right takes you there.
 */

import React, { useEffect, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import { GreatLeapLogoIcon } from './GreatLeapLogoIcon';
import { useApi } from '../lib/api';
import { useTheme } from '../lib/ThemeContext';
import NotificationBell from './NotificationBell';
import BecomePartnerModal from './BecomePartnerModal';
import ConfirmDeleteModal from './workspace/ConfirmDeleteModal';
import '../styles/Notifications.css';

// Shared Zoho Desk helpdesk (also used by the HRMS product).
const SUPPORT_TICKET_URL = 'https://greatleapsupport.zohodesk.in/portal/en/signin';

export default function Header() {
  const { user } = useUser();
  const api = useApi();
  const { theme, toggleTheme } = useTheme();
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [isPartner, setIsPartner] = useState(false);
  const [partnerApplication, setPartnerApplication] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  const loadPartnerState = React.useCallback(async (alive = { current: true }) => {
    try {
      const r = await api.me();
      if (alive.current) { setIsPlatformAdmin(!!r.isPlatformAdmin); setIsPartner(!!r.isPartner); }
    } catch { /* header is non-critical; ignore failures */ }
    try {
      const r = await api.getPartnerApplication();
      if (alive.current) setPartnerApplication(r.application || null);
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => {
    const alive = { current: true };
    loadPartnerState(alive);
    return () => { alive.current = false; };
  }, [loadPartnerState]);

  const displayName = user?.firstName
    ? `${user.firstName} ${user.lastName || ''}`.trim()
    : user?.emailAddresses?.[0]?.emailAddress?.split('@')[0];

  const initials = user?.firstName
    ? `${user.firstName.charAt(0)}${(user.lastName || '').charAt(0)}`.toUpperCase()
    : (user?.emailAddresses?.[0]?.emailAddress?.split('@')[0] || '?').charAt(0).toUpperCase();

  return (
    <header className="app-header">
      <Link to="/dashboard" className="brand-link">
        <GreatLeapLogoIcon size={36} />
      </Link>
      <div className="header-right">
        {isPlatformAdmin && (
          <Link
            to="/admin"
            className="theme-toggle-btn"
            style={{
              color: 'var(--fg)',
              textDecoration: 'none',
              width: 'auto',
              padding: '0 12px',
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              marginRight: 4,
            }}
          >
            Admin
          </Link>
        )}
        {isPartner && (
          <Link
            to="/partner"
            className="theme-toggle-btn"
            style={{
              color: 'var(--fg)',
              textDecoration: 'none',
              width: 'auto',
              padding: '0 12px',
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              marginRight: 4,
            }}
          >
            Partner
          </Link>
        )}
        {!isPartner && (!partnerApplication || partnerApplication.status === 'withdrawn' || partnerApplication.status === 'rejected') && (
          <button
            className="theme-toggle-btn"
            onClick={() => setApplyOpen(true)}
            style={{
              color: 'var(--fg)',
              width: 'auto',
              padding: '0 12px',
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              marginRight: 4,
            }}
          >
            Become a Partner
          </button>
        )}
        {!isPartner && partnerApplication?.status === 'pending' && (
          <span
            className="btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginRight: 4, cursor: 'default', whiteSpace: 'nowrap' }}
            title="Your partner application is under review"
          >
            <span style={{ opacity: 0.7 }}>Application Pending</span>
            <button
              type="button"
              onClick={() => setWithdrawOpen(true)}
              title="Withdraw your partner application"
              style={{ background: 'none', border: 0, padding: 0, color: 'var(--status-danger-fg, #b42318)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
            >
              Withdraw
            </button>
          </span>
        )}
        <a
          href={SUPPORT_TICKET_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="theme-toggle-btn"
          title="Contact Support"
          aria-label="Contact Support"
          style={{
            color: 'var(--fg)',
            textDecoration: 'none',
            width: 'auto',
            padding: '0 12px',
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          Contact Support
        </a>
        <button
          onClick={toggleTheme}
          className="theme-toggle-btn"
          title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          aria-label="Toggle theme"
        >
          {theme === 'light' ? '🌙' : '☀️'}
        </button>
        <NotificationBell />
        <Link
          to="/profile"
          className="header-user-link"
          title="Account & Billing"
        >
          <div className="header-user-avatar">
            {user?.imageUrl
              ? <img src={user.imageUrl} alt={displayName} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              : initials}
          </div>
          <p className="header-user-name">{displayName}</p>
          <span className="header-user-caret">▾</span>
        </Link>
      </div>

      <BecomePartnerModal
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        onSubmitted={() => loadPartnerState()}
      />

      <ConfirmDeleteModal
        open={withdrawOpen}
        title="Withdraw partner application?"
        message="Your application will be removed from the review queue. You can submit a new application later."
        confirmLabel="Withdraw"
        onConfirm={async () => {
          await api.withdrawPartnerApplication();
          await loadPartnerState();
        }}
        onClose={() => setWithdrawOpen(false)}
      />
    </header>
  );
}
